import React from 'react';
import {isUrl} from '@packages/utilities/string-utils';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {Atom, Plugins} from '@packages/systems/wfdl';
import dataTypeFields from './datatypes/twitterDataType';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';

type FollowModeShowCountEnum = 'true' | 'false';

type TweetModeShowCountUnion = {
  tag: 'true' | 'false';
  val: {
    __deprecated__count_layout: 'horizontal' | 'vertical';
  };
};

type TwitterSize = 'm' | 'l';

type FollowMode = {
  screen_name?: string;
  show_count?: FollowModeShowCountEnum;
};

type TweetMode = {
  show_count?: TweetModeShowCountUnion;
  text?: string;
  url?: string;
};

type TwitterMode =
  | {
      tag: 'follow';
      val: FollowMode;
    }
  | {
      tag: 'tweet';
      val: TweetMode;
    };

// Twitter will routinely change the size of it's embedded tweet button
// So when we notice an iframe render size issue we should recheck:
// 1. https://developer.twitter.com/en/docs/twitter-for-websites/tweet-button/overview
// 2. https://developer.twitter.com/en/docs/twitter-for-websites/follow-button/overview
const twitterIFrameButtonSizes: Record<
  TwitterSize,
  {width: number; height: number}
> = {
  m: {
    width: 73,
    height: 20,
  },
  l: {
    width: 81,
    height: 28,
  },
};

const addHttpProtocol = (url: string) =>
  /^http/.test(url) ? url : 'http://' + url;

const encodeParam = ([key, val]: [string, string]) =>
  `${key}=${encodeURIComponent(val)}`;

// Formats iframe params for `follow` mode
const formatFollowModeParams = (
  mode: FollowMode
): {
  src: string;
  params: Record<string, string>;
} => {
  return {
    src: '//platform.twitter.com/widgets/follow_button.html#',
    params: {
      screen_name: mode.screen_name || 'webflow',
      show_count: mode.show_count || 'false',
    },
  };
};

// Formats iframe params for tweet mode
const formatTweetModeParams = (
  mode: TweetMode
): {
  src: string;
  params: Record<string, string>;
} => {
  const show_count =
    (mode.show_count &&
      mode.show_count.val &&
      mode.show_count.val.__deprecated__count_layout) ||
    'false';

  const url =
    mode.url && isUrl(mode.url)
      ? addHttpProtocol(mode.url)
      : 'http://webflow.com';

  // Count URL yields better results without 'http://' (protip!)
  const counturl = url.split('://')[1] || '';

  return {
    src: '//platform.twitter.com/widgets/tweet_button.html#',
    params: {
      url,
      counturl,
      text: mode.text || 'Check out this site',
      show_count,
    },
  };
};

// Add count bubble shim
// https://github.com/webflow/webflow/issues/3322
const shimBubble = (
  size: TwitterSize,
  show_count: string,
  iframe: React.ReactNode
) => {
  const isVertical = show_count === 'vertical';

  const bubbleClasses = ['w-widget-twitter-count-shim'];
  if (size === 'l') bubbleClasses.push('w--large');
  if (isVertical) bubbleClasses.push('w--vertical');

  const bubble = (
    <div className={bubbleClasses.join(' ')} aria-hidden>
      <div className="w-widget-twitter-count-inner">...</div>
    </div>
  );

  // Build HTML based on mode / count type
  if (isVertical) {
    return (
      <>
        {bubble}
        <div className="w-widget-twitter-count-clear" />
        {iframe}
      </>
    );
  }
  return (
    <>
      {iframe}
      {bubble}
    </>
  );
};

const increment = (x: number) => x + 1;

const Twitter = Atom.createRender(
  ({attributes, classes, data, generateAttributes}) => {
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    const {widget} = data;
    const mode: TwitterMode = widget.mode;
    const size: TwitterSize = widget.size;
    const width =
      mode.tag === 'follow' ? '100%' : twitterIFrameButtonSizes[size].width;
    const height = twitterIFrameButtonSizes[size].height;

    const {src, params} =
      mode.tag === 'follow'
        ? formatFollowModeParams(mode.val)
        : formatTweetModeParams(mode.val);

    const queryString = Object.entries({
      ...params,
      dnt: 'true',
      size,
      width: String(width),
      height: String(height),
    })
      .map(encodeParam)
      .join('&');

    const iframeSrc = `${src}${queryString}`;

    // Whenever the iframe `src` changes we need to reload the iframe for the
    // changes to take effect and we do that by incrementing the `key`
    // and forcing React to re-render the iframe.
    const [iframeKey, setIframeKey] = React.useState(0);
    React.useLayoutEffect(() => {
      setIframeKey(increment);
    }, [iframeSrc]);

    const iframe = (
      <iframe
        key={iframeKey}
        title={
          mode.tag === 'follow'
            ? 'Twitter Follow Button'
            : 'Twitter Tweet Button'
        }
        src={iframeSrc}
        frameBorder="0"
        scrolling="no"
        // eslint-disable-next-line react/no-unknown-property
        allowTransparency={'true' as any}
        style={{
          border: 'none',
          width,
          height,
          overflow: 'hidden',
        }}
      />
    );

    const show_count = params.show_count;

    // We only shim the count bubble when count is enabled and tweetMode isn't
    // 'follow' because follow mode supports the count button by default. The
    // other modes do not, so we shim the bubble with custom appended HTML / CSS.
    // https://github.com/webflow/webflow/issues/3322
    const children =
      mode.tag !== 'follow' &&
      (show_count === 'horizontal' || show_count === 'vertical')
        ? shimBubble(size, show_count, iframe)
        : iframe;

    const twitterClasses = classes.push('w-widget').push('w-widget-twitter');

    return (
      <div {...generateAttributes(attributes, twitterClasses)}>{children}</div>
    );
  }
);

const config = createDevlinkAtom({
  displayName: 'X (Twitter)',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Widget'), iconName('Twitter')),
  },
  showEmpty: false,
  render: Twitter,
  fields: dataTypeFields,
});

export default config;
